/**
 * Apple Health (HealthKit) / Google Health Connect sync for the Capacitor shell.
 *
 * Read-only: body weight, steps, sleep and resting heart rate. HealthKit and
 * Health Connect have no server API, so the phone reads the data, buckets it
 * into LOCAL days (a client's Tuesday is their Tuesday, whatever the server's
 * clock says) and posts daily rollups to /api/v1/device_health/batch.
 *
 * The native plugin is @capgo/capacitor-health, installed in the shell repo
 * (TurnkeyCoach_Capacitor), reached through the injected
 * `window.Capacitor.Plugins.Health` global like ScreenRecorder and
 * VideoCompressor. App builds without it report the plugin unavailable, so the
 * opt-in page hides its Connect button and nothing here runs.
 *
 * Permission is ONLY requested from the opt-in page
 * (/integrations/device_health) via connectDeviceHealth(). The background sync
 * (registerDeviceHealthSync) never prompts, and runs only when BOTH the server
 * says this user is connected AND this phone is the one they connected on: the
 * health grant belongs to the phone, so another account signed in on the same
 * phone (or an admin impersonating) must never push this phone's data into
 * theirs. Behind the `device_health_ingestion` flag. Every export is a no-op
 * off the shell and never throws out of an event handler.
 */

import axios from "axios";
import Honeybadger from "@honeybadger-io/js";
import {
  capacitorPlatform,
  isCapacitorEnvironment,
  isCapacitorPluginAvailable,
} from "./detection";

export const HEALTH_PLUGIN_NAME = "Health";
export const DEVICE_HEALTH_FLAG = "device_health_ingestion";
export const READ_SCOPES = ["weight", "steps", "sleep", "restingHeartRate"];

// First sync after connecting backfills this many days; later syncs re-send
// from the last sync minus an overlap (sleep and steps land late: a watch can
// upload last night hours after waking), capped at the backfill window.
export const BACKFILL_DAYS = 30;
const RESYNC_OVERLAP_DAYS = 3;
// Foreground syncs (and the status check before them) are throttled so app
// switching doesn't hammer HealthKit and the API.
const MIN_SYNC_INTERVAL_MS = 3 * 60 * 60 * 1000;
const SAMPLE_LIMIT = 5000;
// Sleep states that count as asleep. inBed and awake do not.
const ASLEEP_STATES = new Set(["asleep", "rem", "deep", "light"]);
// Asleep stretches closer together than this belong to the same night (a
// wake-up at 3am does not start a new night); further apart is a new session
// (a nap).
const SLEEP_SESSION_GAP_MS = 2 * 60 * 60 * 1000;

function reportError(error) {
  try {
    Honeybadger.notify(error, {
      context: { component: "capacitor_health_sync" },
    });
  } catch {
    // reporting must never break the app
  }
}

function flagOn() {
  try {
    return (
      Array.isArray(window._tkcEnabledFeatures) &&
      window._tkcEnabledFeatures.includes(DEVICE_HEALTH_FLAG)
    );
  } catch {
    return false;
  }
}

function getHealthPlugin() {
  try {
    return window.Capacitor?.Plugins?.[HEALTH_PLUGIN_NAME] || null;
  } catch {
    return null;
  }
}

function currentUserId() {
  try {
    return window._tkcCurrentUserId ? String(window._tkcCurrentUserId) : null;
  } catch {
    return null;
  }
}

// Per-user keys: two accounts on one phone keep separate state.
function storageKey(name) {
  return `tkc.deviceHealth.${name}.${currentUserId() || "anon"}`;
}

function readStorage(name) {
  try {
    return window.localStorage.getItem(storageKey(name));
  } catch {
    return null;
  }
}

function writeStorage(name, value) {
  try {
    if (value === null) {
      window.localStorage.removeItem(storageKey(name));
    } else {
      window.localStorage.setItem(storageKey(name), value);
    }
  } catch {
    // storage disabled: the throttle and device marker just don't persist
  }
}

/**
 * The server-side source for this phone: "health_kit" on iOS,
 * "health_connect" on Android, null anywhere else.
 * @returns {"health_kit"|"health_connect"|null}
 */
export function deviceHealthSource() {
  const platform = capacitorPlatform();
  if (platform === "ios") return "health_kit";
  if (platform === "android") return "health_connect";
  return null;
}

/**
 * Whether this app build can connect to Apple Health / Health Connect: in the
 * shell, on iOS or Android, with the native plugin installed, flag on.
 * @returns {boolean}
 */
export function isDeviceHealthAvailable() {
  return (
    isCapacitorEnvironment() &&
    flagOn() &&
    deviceHealthSource() !== null &&
    isCapacitorPluginAvailable(HEALTH_PLUGIN_NAME) &&
    getHealthPlugin() !== null
  );
}

/** Whether the signed-in user connected health on THIS phone. */
export function connectedOnThisDevice() {
  return readStorage("connected") === deviceHealthSource();
}

function pad(number) {
  return String(number).padStart(2, "0");
}

/** The LOCAL calendar date of a timestamp, as YYYY-MM-DD. */
export function localDay(isoOrDate) {
  const date = isoOrDate instanceof Date ? isoOrDate : new Date(isoOrDate);
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

// The local day an aggregate bucket covers, read at its MIDPOINT: Health
// Connect builds `day` buckets as fixed 24h spans, so after a DST change a
// bucket starts at 23:00 or 01:00 local and its start alone names the wrong day.
function bucketDay(bucket) {
  const start = new Date(bucket.startDate).getTime();
  const end = bucket.endDate ? new Date(bucket.endDate).getTime() : start;
  return localDay(new Date(start + (end - start) / 2));
}

function unionIntervals(intervals) {
  const sorted = [...intervals].sort((a, b) => a[0] - b[0]);
  const merged = [];
  sorted.forEach(([start, end]) => {
    const last = merged[merged.length - 1];
    if (last && start <= last[1]) {
      if (end > last[1]) last[1] = end;
    } else {
      merged.push([start, end]);
    }
  });
  return merged;
}

function asleepIntervals(sleep) {
  const intervals = [];
  sleep.forEach((sample) => {
    if (!sample?.startDate || !sample?.endDate) return;
    // A Health Connect sample is a whole session carrying stage segments; a
    // HealthKit sample is one segment with a sleepState.
    const segments =
      Array.isArray(sample.stages) && sample.stages.length > 0
        ? sample.stages.map((stage) => [
            stage.startDate,
            stage.endDate,
            stage.stage,
          ])
        : [[sample.startDate, sample.endDate, sample.sleepState || "asleep"]];
    segments.forEach(([startDate, endDate, state]) => {
      if (!ASLEEP_STATES.has(state)) return;
      const start = new Date(startDate);
      const end = new Date(endDate);
      if (end > start) intervals.push([start, end]);
    });
  });
  return intervals;
}

/**
 * Group asleep time into sessions (nights and naps). Overlapping segments from
 * two sources (an iPhone and a Watch) are counted once, and segments of one
 * night stay together across midnight.
 * @returns {Array<{start:Date,end:Date,minutes:number}>}
 */
export function sleepSessions(sleep) {
  const sessions = [];
  unionIntervals(asleepIntervals(sleep)).forEach(([start, end]) => {
    const minutes = (end - start) / 60000;
    const last = sessions[sessions.length - 1];
    if (last && start - last.end < SLEEP_SESSION_GAP_MS) {
      last.end = end;
      last.minutes += minutes;
    } else {
      sessions.push({ start, end, minutes });
    }
  });
  return sessions;
}

/**
 * Fold plugin results into one rollup per local day, in the shape
 * Api::V1::DeviceHealthController#batch accepts. Pure, so it is unit-tested
 * directly.
 *
 * - steps: the day's sum bucket.
 * - restingHeartRate: the day's average bucket, rounded.
 * - weight: the LAST reading of the day, in kilograms ("kgs").
 * - sleep: asleep minutes per session (inBed/awake excluded, overlapping
 *   sources counted once), each session credited to the day it ENDED (waking
 *   on Tuesday is Tuesday's sleep), with the day's earliest start and latest
 *   end.
 *
 * @param {string|null} fromDay drop days before this (YYYY-MM-DD): sleep is
 *   read from a day earlier so the first night is whole, and that extra day
 *   would otherwise be sent with only part of its data
 * @returns {Array<object>} days sorted ascending; days with no data omitted
 */
export function buildDailyRollups(
  { steps = [], restingHeartRate = [], weight = [], sleep = [] },
  fromDay = null,
) {
  const days = new Map();
  const dayFor = (key) => {
    if (!days.has(key)) days.set(key, { day: key });
    return days.get(key);
  };

  steps.forEach((bucket) => {
    if (typeof bucket?.value !== "number") return;
    dayFor(bucketDay(bucket)).steps = Math.round(bucket.value);
  });

  restingHeartRate.forEach((bucket) => {
    if (typeof bucket?.value !== "number") return;
    dayFor(bucketDay(bucket)).resting_heart_rate = Math.round(bucket.value);
  });

  const latestWeight = new Map();
  weight.forEach((sample) => {
    if (typeof sample?.value !== "number" || sample.unit !== "kilogram") return;
    const key = localDay(sample.startDate);
    const previous = latestWeight.get(key);
    if (
      !previous ||
      new Date(sample.startDate) > new Date(previous.startDate)
    ) {
      latestWeight.set(key, sample);
    }
  });
  latestWeight.forEach((sample, key) => {
    const entry = dayFor(key);
    entry.weight = Math.round(sample.value * 100) / 100;
    entry.weight_unit = "kgs";
  });

  sleepSessions(sleep).forEach((session) => {
    const entry = dayFor(localDay(session.end));
    entry.sleep_minutes = (entry.sleep_minutes || 0) + session.minutes;
    const start = session.start.toISOString();
    const end = session.end.toISOString();
    if (!entry.sleep_start_time || start < entry.sleep_start_time) {
      entry.sleep_start_time = start;
    }
    if (!entry.sleep_end_time || end > entry.sleep_end_time) {
      entry.sleep_end_time = end;
    }
  });

  return Array.from(days.values())
    .filter((entry) => !fromDay || entry.day >= fromDay)
    .map((entry) =>
      typeof entry.sleep_minutes === "number"
        ? { ...entry, sleep_minutes: Math.round(entry.sleep_minutes) }
        : entry,
    )
    .sort((a, b) => (a.day < b.day ? -1 : 1));
}

// One read per metric. A metric the user declined (or one the platform has no
// data for) rejects or comes back empty; that metric is skipped and the rest
// still sync. Not reported: a declined scope is a normal state on every sync,
// not an error.
async function readMetric(promise) {
  try {
    const result = await promise;
    return result?.samples || [];
  } catch {
    return [];
  }
}

async function readRollups(plugin, startDate, endDate) {
  const range = {
    startDate: startDate.toISOString(),
    endDate: endDate.toISOString(),
  };
  const sleepStart = new Date(startDate);
  sleepStart.setDate(sleepStart.getDate() - 1);
  // Newest first, so if a heavy multi-app user ever hits SAMPLE_LIMIT it is
  // the oldest days that are cut, not last night.
  const [steps, restingHeartRate, weight, sleep] = await Promise.all([
    readMetric(
      plugin.queryAggregated({
        ...range,
        dataType: "steps",
        bucket: "day",
        aggregation: "sum",
      }),
    ),
    readMetric(
      plugin.queryAggregated({
        ...range,
        dataType: "restingHeartRate",
        bucket: "day",
        aggregation: "average",
      }),
    ),
    readMetric(
      plugin.readSamples({
        ...range,
        dataType: "weight",
        limit: SAMPLE_LIMIT,
        ascending: false,
      }),
    ),
    readMetric(
      plugin.readSamples({
        ...range,
        startDate: sleepStart.toISOString(),
        dataType: "sleep",
        limit: SAMPLE_LIMIT,
        ascending: false,
      }),
    ),
  ]);
  return buildDailyRollups(
    { steps, restingHeartRate, weight, sleep },
    localDay(startDate),
  );
}

/**
 * The first day to (re)send: the backfill window on a first sync, otherwise
 * the last sync minus an overlap for late-arriving data, never further back
 * than the backfill window.
 */
export function syncStartDate(lastSyncedAt, now = new Date()) {
  const today = startOfLocalDay(now);
  const backfillStart = new Date(today);
  backfillStart.setDate(backfillStart.getDate() - (BACKFILL_DAYS - 1));
  if (!lastSyncedAt) return backfillStart;

  const resyncStart = startOfLocalDay(new Date(lastSyncedAt));
  resyncStart.setDate(resyncStart.getDate() - RESYNC_OVERLAP_DAYS);
  return resyncStart > backfillStart ? resyncStart : backfillStart;
}

/**
 * Read and upload this phone's health data. Assumes permission was already
 * granted; never prompts.
 * @returns {Promise<{days_upserted:number}|null>} null when nothing was sent
 */
export async function syncDeviceHealth({ lastSyncedAt = null } = {}) {
  if (!isDeviceHealthAvailable()) return null;
  const plugin = getHealthPlugin();
  const source = deviceHealthSource();
  const now = new Date();
  const days = await readRollups(plugin, syncStartDate(lastSyncedAt, now), now);
  if (days.length === 0) return null;

  const response = await axios.post("/api/v1/device_health/batch", {
    source,
    days,
  });
  return response.data;
}

function markSyncAttempt() {
  writeStorage("lastSyncAttempt", String(Date.now()));
}

function recentlyAttempted() {
  const last = Number(readStorage("lastSyncAttempt"));
  return Number.isFinite(last) && Date.now() - last < MIN_SYNC_INTERVAL_MS;
}

/**
 * The opt-in flow, run from the Connect button on /integrations/device_health:
 * show the native permission sheet, record the connection, then backfill.
 * Rejects with a user-presentable Error (`userFacing: true`) when the store is
 * unavailable or nothing was granted. A failed first sync does NOT undo the
 * connection: it is returned as `syncError` and the next app open retries.
 */
export async function connectDeviceHealth() {
  if (!isDeviceHealthAvailable()) {
    throw new Error("Apple Health / Health Connect is not available here.");
  }
  const plugin = getHealthPlugin();
  const source = deviceHealthSource();

  const availability = await plugin.isAvailable();
  if (!availability?.available) {
    const error = new Error(
      source === "health_connect"
        ? "Health Connect isn't available on this phone. Install or update it from the Play Store, then try again."
        : "Apple Health isn't available on this device.",
    );
    error.userFacing = true;
    throw error;
  }

  const status = await plugin.requestAuthorization({
    read: READ_SCOPES,
    write: [],
    requestHistoryAccess: source === "health_connect",
  });
  // iOS never reveals a READ denial (it reports what was asked), so this is a
  // best-effort record there; Android reports real grants.
  const granted = (status?.readAuthorized || []).filter((scope) =>
    READ_SCOPES.includes(scope),
  );
  if (source === "health_connect" && granted.length === 0) {
    const error = new Error(
      "No Health Connect permissions were granted, so there is nothing to share. You can try again any time.",
    );
    error.userFacing = true;
    throw error;
  }

  // Throttle BEFORE the first sync: the permission sheet backgrounds the app,
  // and the foreground sync on return must not race this one.
  markSyncAttempt();
  const connection = await axios.post("/api/v1/device_health/connect", {
    source,
    granted_scopes: granted,
  });
  writeStorage("connected", source);

  let synced = null;
  let syncError = null;
  try {
    synced = await syncDeviceHealth();
  } catch (error) {
    reportError(error);
    syncError = error;
  }
  return { connection: connection.data, synced, syncError, granted };
}

/** Stop syncing from this phone's store. Data already sent is kept. */
export async function disconnectDeviceHealth(source = deviceHealthSource()) {
  const response = await axios.delete("/api/v1/device_health/connect", {
    data: { source },
  });
  writeStorage("connected", null);
  return response.data;
}

let syncInFlight = false;

/**
 * Sync in the background if this user connected on this phone. Never prompts,
 * throttled (status check included), at most one run at a time. Exported for
 * tests.
 */
export async function backgroundSync() {
  if (
    syncInFlight ||
    !isDeviceHealthAvailable() ||
    !connectedOnThisDevice() ||
    recentlyAttempted()
  ) {
    return;
  }
  syncInFlight = true;
  markSyncAttempt();
  try {
    const source = deviceHealthSource();
    const { data } = await axios.get("/api/v1/device_health/status");
    if (!data?.[source]?.connected) {
      // Disconnected elsewhere (or on the web): forget this phone's marker.
      writeStorage("connected", null);
      return;
    }
    await syncDeviceHealth({ lastSyncedAt: data[source].last_synced_at });
  } catch (error) {
    reportError(error);
  } finally {
    syncInFlight = false;
  }
}

let registered = false;

/**
 * Sync on launch and whenever the app returns to the foreground. No-op off
 * the shell, without the plugin or without the flag; runs once; never throws.
 */
export function registerDeviceHealthSync() {
  if (registered || !isDeviceHealthAvailable()) {
    return;
  }
  registered = true;
  backgroundSync();
  import("@capacitor/app")
    .then(({ App }) => {
      const result = App.addListener("appStateChange", ({ isActive }) => {
        if (isActive) backgroundSync();
      });
      if (result && typeof result.catch === "function") {
        result.catch(reportError);
      }
    })
    .catch((error) => {
      registered = false; // allow a later call to retry
      reportError(error);
    });
}
